<template>
  <div class="twitter-feed p-6 bg-background text-foreground">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-xl font-semibold">Tweets</h2>
      <Button variant="outline" size="sm" :disabled="isSharingSet" @click="handleShareSet">
        <Icon v-if="!isSharingSet" name="heroicons:share" class="h-4 w-4 mr-1.5" />
        <Icon v-else name="heroicons:arrow-path" class="h-4 w-4 mr-1.5 animate-spin" />
        {{ isSharingSet ? 'Creating Link...' : 'Share Tweet Set' }}
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex justify-center py-4">
      <span class="loading loading-spinner"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-4 text-destructive">
      <p>{{ error }}</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="sortedTweets.length === 0" class="text-center py-8">
      <p class="text-muted-foreground mb-4">You don't have any tweets yet.</p>
      <Button @click="router.push('/dashboard/add')">Add Your First Tweet</Button>
    </div>

    <!-- Content -->
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <AppLink
        v-for="tweet in sortedTweets"
        :key="tweet.uuid"
        :to="`/bubble/${tweet.uuid}`"
        class="block h-full"
      >
        <div class="aspect-[1/1.5] h-full overflow-hidden">
          <TweetCard :tweet="tweet" class="h-full" />
        </div>
      </AppLink>
    </div>

    <!-- Load More Button -->
    <div v-if="canLoadMore" class="mt-8 text-center">
      <Button :disabled="isLoading" variant="default" class="hover:opacity-90" @click="loadMore">
        Load More Tweets
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch, h } from 'vue'
import { useBubblesStore } from '@/stores/bubbles'
import { useUserShareStore } from '~/stores/userShare'
import { useRouter } from 'vue-router'
import TweetCard from '@/components/tweet/TweetCard.vue'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/toast'
import CopyButton from '@/components/ui/button/CopyButton.vue'
import { useNavigationStore } from '@/stores/navigationStore'
import type { TwitterBubble } from '@/stores/interfaces'
import AppLink from '@/components/ui/link/AppLink.vue'

const TWEETS_PER_PAGE = 12

const bubblesStore = useBubblesStore()
const userShareStore = useUserShareStore()
const { toast } = useToast()
const router = useRouter()
const isLoading = ref(false)
const error = ref<string | null>(null)
const page = ref(1)
const isSharingSet = ref(false)

const twitterBubbles = computed(() => {
  const bubbles = bubblesStore.bubblesByType['twitter'] || []
  return bubbles as TwitterBubble[]
})

const sortedTweets = computed(() => {
  return [...twitterBubbles.value]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, page.value * TWEETS_PER_PAGE)
})

const canLoadMore = computed(() => {
  return twitterBubbles.value.length > page.value * TWEETS_PER_PAGE
})

async function loadMore() {
  page.value++
  if (page.value * TWEETS_PER_PAGE >= twitterBubbles.value.length) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('twitter')
    } catch (err) {
      error.value = 'Failed to load more tweets. Please try again later.'
      logError('Failed to fetch tweets:', err)
    } finally {
      isLoading.value = false
    }
  }
}

onMounted(async () => {
  if (
    !bubblesStore.bubblesByType['twitter'] ||
    bubblesStore.bubblesByType['twitter'].length < TWEETS_PER_PAGE
  ) {
    try {
      isLoading.value = true
      error.value = null
      await bubblesStore.fetchBubblesByType('twitter')
    } catch (err) {
      error.value = 'Failed to load tweets. Please try again later.'
      logError('Failed to fetch tweets:', err)
    } finally {
      isLoading.value = false
    }
  }
})

const navigationStore = useNavigationStore()

// Store the sorted UUIDs whenever they change
watch(sortedTweets, (tweets) => {
  navigationStore.setCurrentType('twitter')
  navigationStore.setCurrentTypeItems(tweets.map((tweet) => tweet.uuid))
})

// --- Share Set Logic --- //
const handleShareSet = async () => {
  isSharingSet.value = true
  try {
    const shareData = {
      shareType: 'set' as const,
      targetBubbleType: 'twitter', // Type for this view
      isPublic: true,
    }
    const result = await userShareStore.createShareLink(shareData)

    if (result && result.url) {
      toast({
        title: 'Share Link Created',
        description: 'Your set of tweets can be shared via this link.',
        duration: 9000,
        action: h(CopyButton, { textToCopy: result.url, variant: 'secondary' }),
      })
    } else {
      if (!result) {
        toast({
          title: 'Error',
          description: 'Could not create share link for set.',
          variant: 'destructive',
        })
      }
      // Store handles other errors
    }
  } catch (error) {
    logError('Error creating tweet set share link:', error)
    toast({
      title: 'Error',
      description: 'An unexpected error occurred while sharing the set.',
      variant: 'destructive',
    })
  } finally {
    isSharingSet.value = false
  }
}
</script>

<style scoped>
.twitter-feed {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
}

:deep(.tweet-card) {
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
